import { Button, Flex, Modal, notification } from "antd";
import { saveAs } from "file-saver";
import { useState } from "react";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx-js-style";
import {
  buildPackingExcelData,
  packingExcelColumnWidths,
  packingExcelColumns,
  packingExcelHeaders,
} from "./packingExcel";
import type { PackingExportData } from "./packingExcel";

interface PackingExportButtonProps {
  order?: PackingExportData;
  disabled?: boolean;
  onConfirm?: () => Promise<PackingExportData | undefined>;
}

type PackingExcelData = ReturnType<typeof buildPackingExcelData>;

/**
 * 装箱单包含多行表头、合并单元格和单元格样式，无法使用普通列表导出，
 * 因此在装箱模块内按固定格式生成并下载 Excel。
 */
const exportPackingExcel = (data: PackingExcelData, fileName: string) => {
  const dataRows = data.rows.map((row) => (
    packingExcelColumns.map((column) => row[column.key])
  ));
  const worksheet = XLSX.utils.aoa_to_sheet([...packingExcelHeaders, ...dataRows]);

  worksheet["!merges"] = data.merges;
  worksheet["!cols"] = packingExcelColumnWidths.map((width) => ({ wch: width }));
  if (worksheet["!ref"]) {
    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    worksheet["!rows"] = Array.from({ length: range.e.r + 1 }, () => ({ hpt: 18 }));
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Packing List");
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const dataBlob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
  });
  saveAs(dataBlob, fileName);
};

const PackingExportButton: FC<PackingExportButtonProps> = ({ order, disabled, onConfirm }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (disabled || exporting) return;
    setExporting(true);
    // 先让确认按钮渲染 loading，再执行同步的数据解析和 Excel 生成。
    await new Promise<void>((resolve) => window.setTimeout(resolve, 0));

    try {
      // 仅在用户确认导出后解析装箱数据，避免打开订单详情时提前计算。
      const exportOrder = onConfirm ? await onConfirm() : order;
      if (!exportOrder) return;
      const data = buildPackingExcelData(exportOrder);
      exportPackingExcel(data, `${exportOrder.number}_${t("warehouse.packing")}.xlsx`);
      setOpen(false);
    } catch (error) {
      console.error(`${t("common.exportErr")}:`, error);
      notification.error({ message: t("common.exportErr") });
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Button disabled={disabled || exporting} onClick={() => setOpen(true)}>
        {t("warehouse.exportExcel")}
      </Button>
      <Modal
        title={t("warehouse.exportExcel")}
        open={open}
        onCancel={() => { if (!exporting) setOpen(false); }}
        closable={!exporting}
        footer={(
          <Flex justify="flex-end" gap={8}>
            <Button disabled={exporting} onClick={() => setOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="primary" disabled={disabled} loading={exporting} onClick={handleExport}>
              {t("common.confirm")}
            </Button>
          </Flex>
        )}
      >
        {t("warehouse.packingExportConfirm")}
      </Modal>
    </>
  );
};

export default PackingExportButton;
